import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import classnames from 'classnames';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { MpgEntities } from 'client/data/models/mpg';
import { calculateTailpipeEmissions } from 'site-modules/shared/utils/range-and-cost';
import { getFuelTypeIcons } from 'site-modules/shared/components/mpg/utils/mpg';

export function TailpipeEmissions({
  modelName,
  averageCategory,
  style: {
    engineType,
    mpgData,
    evData: { range },
  },
  fuelCostsData,
  personalizationParams: { milesPerYear, cityPercents, dailyCommute },
  isPHEV,
  isElectric,
}) {
  const averageEmissions = calculateTailpipeEmissions({
    primaryFuelGrade: 'R',
    cityPercents,
    milesPerYear,
    mpgData: {
      city: get(fuelCostsData, 'statistics.byBodyTypeAndSize.combustionEngines.averageEpaCityMPG'),
      highway: get(fuelCostsData, 'statistics.byBodyTypeAndSize.combustionEngines.averageEpaHwyMPG'),
    },
  });

  if (isElectric) {
    return (
      <div className="tailpipe-emissions">
        <div className={classnames('heading-4 text-center', { 'mb-1': averageEmissions })}>
          <i className="size-20 icon-leaf text-turquoise-50" aria-hidden /> 0 Emissions!
        </div>
        {!!averageEmissions && (
          <div className="medium">
            A similar {averageCategory || 'gas vehicle'} emits {parseFloat(averageEmissions).toFixed(1)} tons of CO2 per
            year.
          </div>
        )}
      </div>
    );
  }

  const emissions = calculateTailpipeEmissions({
    primaryFuelGrade: get(fuelCostsData, 'fuelCosts.primary.fuelGrade') || get(fuelCostsData, 'fuelCosts.fuelGrade'),
    engineType,
    dailyCommute,
    cityPercents,
    milesPerYear,
    range,
    mpgData,
  });

  return (
    <div className="tailpipe-emissions">
      <Row className="pos-r mb-1_5">
        <Col xs={6} className="text-center">
          {getFuelTypeIcons({ isPHEV }).map(fuelTypeIcon => (
            <i key={fuelTypeIcon} className={classnames('size-24 type-icon mb-0_5', fuelTypeIcon)} aria-hidden />
          ))}
          <div className="heading-4">
            {Number.isFinite(emissions) ? (
              <Fragment>
                {parseFloat(emissions.toFixed(1))} tons CO<span className="medium">2</span>
              </Fragment>
            ) : (
              'Not Available'
            )}
          </div>
          <div className="medium">{modelName}</div>
        </Col>
        <div className="vs-separator pos-a center-x h-100 p-0 m-0 w-auto">
          <div className="pos-a center-xy bg-white fw-bold text-gray-dark medium py-0_25">vs</div>
        </div>
        <Col xs={6} className="text-center">
          <i className="icon-gas size-24 text-cool-gray-50 mb-0_5" aria-hidden />
          <div className="size-20 fw-bold text-cool-gray-50">
            {Number.isFinite(averageEmissions) ? (
              <Fragment>{parseFloat(averageEmissions.toFixed(1))} tons</Fragment>
            ) : (
              'Not Available'
            )}
          </div>
          <div className="medium">{averageCategory ? `Avg. ${averageCategory}` : 'Similar Gas Vehicles'}</div>
        </Col>
      </Row>
      {(Number.isFinite(averageEmissions) || Number.isFinite(emissions)) && (
        <div className="medium">Yearly estimate based on your driving miles.</div>
      )}
    </div>
  );
}

TailpipeEmissions.propTypes = {
  style: MpgEntities.StyleMpg.isRequired,
  personalizationParams: PropTypes.shape({
    dailyCommute: PropTypes.number,
    milesPerYear: PropTypes.number,
    cityPercents: PropTypes.number,
  }).isRequired,
  fuelCostsData: VehicleEntities.StyleFuelCostData,
  modelName: PropTypes.string,
  averageCategory: PropTypes.string,
  isPHEV: PropTypes.bool,
  isElectric: PropTypes.bool,
};

TailpipeEmissions.defaultProps = {
  fuelCostsData: null,
  modelName: null,
  averageCategory: null,
  isPHEV: false,
  isElectric: false,
};
